import React from 'react';
import { Box, Paper, Typography, Grid, Button, TextField, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import VoiceInput from './VoiceInput';
import { formatCurrency } from '../utils/formatHelpers';
import { fetchCommands, fetchExamples, processCommand } from '../slices/voiceSlice';

const VoiceCommands = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const commands = useSelector((state) => state.voice.commands);
  const examples = useSelector((state) => state.voice.examples);
  const loading = useSelector((state) => state.voice.loading);
  const error = useSelector((state) => state.voice.error);

  React.useEffect(() => {
    // Fetch commands and examples when component mounts
    dispatch(fetchCommands());
    dispatch(fetchExamples());
  }, [dispatch]);

  const handleCommand = async (text) => {
    try {
      const response = await dispatch(processCommand(text)).unwrap();
      if (response.success) {
        // Handle successful command execution
        navigate('/transactions');
      }
    } catch (error) {
      console.error('Command failed:', error);
    }
  };

  const commandCategories = [
    'Transactions',
    'Budgets',
    'Debts',
    'Reports',
    'Settings',
  ];

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Voice Commands
      </Typography>

      {/* Voice Input Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Voice Input
        </Typography>
        <VoiceInput onCommand={handleCommand} />
      </Paper>

      {/* Command Examples */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Command Examples
        </Typography>
        <Grid container spacing={3}>
          {commandCategories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {category}
                  </Typography>
                  <ul>
                    {examples[category.toLowerCase()]?.map((example, index) => (
                      <li key={index}>
                        <Typography variant="body2" color="text.secondary">
                          {example}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Command History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Recent Commands
        </Typography>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <Grid container spacing={2}>
            {commands.map((command) => (
              <Grid item xs={12} sm={6} md={4} key={command.id}>
                <Card>
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {command.timestamp}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {command.text}
                    </Typography>
                    <Typography variant="body2" color={command.success ? 'success' : 'error'}>
                      {command.success ? 'Success' : 'Failed'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
};

export default VoiceCommands;
